import { useEffect, useState } from 'react';
import pb, { pbPortal, pbEngineer, activePb, onScopeChange } from './pb';

// ------------------------------------------------------------------
// Session helpers. Three independent sessions may exist at once:
//   - admin    → pb           (storage key "pp_admin")
//   - client   → pbPortal     (storage key "pp_portal") 
//   - engineer → pbEngineer   (storage key "pp_engineer")
// useSession() reads whichever client is ACTIVE for the current route
// (App flips the scope on navigation between /admin, /portal, /engineer).
// ------------------------------------------------------------------

function sessionOf(client) {
  const record = client.authStore.record;
  if (!record) {
    return { role: null, tenant: null, client: null, isLoading: false };
  }
  return {
    role: record.role || null,
    tenant: record.tenant_id || null,
    client: record.client_id || null,
    isLoading: false,
  };
}

// Mirrors frontend.md §4: { role, tenant, client, isLoading }.
// No default role — unauthenticated is null so gates redirect properly.
export function useSession() {
  const [session, setSession] = useState(() => ({
    role: null,
    tenant: null,
    client: null,
    isLoading: true,
  }));

  useEffect(() => {
    setSession(sessionOf(activePb()));
    const unbindActive = onScopeChange(() => setSession(sessionOf(activePb())));
    const unbindAdmin = pb.authStore.onChange(() => setSession(sessionOf(activePb())));
    const unbindPortal = pbPortal.authStore.onChange(() => setSession(sessionOf(activePb())));
    const unbindEngineer = pbEngineer.authStore.onChange(() => setSession(sessionOf(activePb())));
    return () => {
      unbindActive?.();
      unbindAdmin?.();
      unbindPortal?.();
      unbindEngineer?.();
    };
  }, []);

  return session;
}

export const isAgencyRole = (role) => role === 'agency_admin' || role === 'agency_staff';

// Agency staff/admin login — same PocketBase `accounts` collection the
// portal uses; clients authenticate via magic-link or email+password.
export async function signInAgency(email, password) {
  return pb.collection('accounts').authWithPassword(email, password);
}

// Client login — full email+password auth into the dedicated portal
// store so it can never clobber an admin session (E1/E5).
export async function signInClient(email, password) {
  return pbPortal.collection('accounts').authWithPassword(email, password);
}

// Engineer login — full email+password auth into the dedicated engineer
// store so it can never clobber admin or portal sessions.
export async function signInEngineer(email, password) {
  return pbEngineer.collection('accounts').authWithPassword(email, password);
}

export function signOutAdmin() {
  pb.authStore.clear();
}

export function signOutClient() {
  pbPortal.authStore.clear();
}

export function signOutEngineer() {
  pbEngineer.authStore.clear();
}

// Back-compat: sign out whichever session is active on this route.
export function signOut() {
  activePb().authStore.clear();
}
